import {Types}             from "@Common/types";
import {changeNestedValue} from "@Common/utils/arr-path";
import {chain}             from "@Common/utils/fs";
import {omit}              from "@Common/utils/objects";

export const CartApis = ({authFetcher}) => {
    const fixCartResponse = (cart) => {
        if (cart == null || Array.isArray(cart) && cart.length === 0) {
            return null;
        }
        if (cart.items?.[0] === "") {
            cart = changeNestedValue(cart, ["items"], () => []);
        }
        return cart;
    };
    return {
        getCurrentCart: async ():Promise<Types.Apis.Payloads.Next.Cart.GetCurrent.Response> => chain(
            await (async () => {
                const result = await authFetcher.getJson(`/next-api/cart/get_current_cart`);
                if (result?.result === "failure") {
                    throw result.errors?.msg;
                }
                return result;
            })(),
            fixCartResponse,
        ),

        validateCart: async (cartId) => chain(
            await (async () => {
                const result = await authFetcher.postJson(`/next-api/cart/validate`, {
                    cartId,
                });
                if (result?.result === "failure") {
                    throw result.errors?.msg ?? result.errors?.[0]?.msg;
                }
                return result;
            })(),
            fixCartResponse,
        ),

        emptyCart: async (cartId) => {
            const result = await authFetcher.postJson(`/next-api/cart/empty_cart`, {
                cartId: "" + cartId,
            });
            if (result.result === "failure") {
                throw result.errors.msg;
            }
            return result;
        },

        createCart: async (cartType) => {
            return await authFetcher.postJson(`/next-api/cart/create`, {
                cartType: "" + cartType,
            });
        },

        createApprovalCart: async() => {
            return await authFetcher.postJson(`/next-api/cart/create`, {
                "cartType": "MAIN",
                "isApproval": true,
            });
        },

        addItemsToCart: async ({cartId, itemIds}) => {
            let result;
            for (const itemId of itemIds) {
                result = await authFetcher.postJson(`/next-api/cart/add_items_to_cart`, {
                    cartId: "" + cartId,
                    itemId: "" + itemId
                });
            }
            return result;
        },

        /**
         * This will not return full cart object, only {"result":0,"message":"success"}
         */
        deleteItemsFromCart: async ({cartId, itemIds}) => {
            let result;
            for (const itemId of itemIds) {
                result = await authFetcher.postJson(`/next-api/cart/delete_items_from_cart`, {
                    cartId: "" + cartId,
                    itemId: "" + itemId
                });
            }
            return result;
        },

        /**
         * Returns full cart
         */
        updateCart: async (cart) => {
            return await authFetcher.postJson(`/next-api/cart/update_items_in_cart`, chain(
                cart,
                (_) => omit(_, [
                    "items", "itemIds", "appliedPayments",
                    // "appliedCredits",
                ]),
            ));
        },

        checkoutCart: async (cartId) => {
            return await authFetcher.postJson(`/next-api/checkout/checkout_cart`, {"cartId": "" + cartId});
        },
        getpdfs: async (cartId) => {
            return await authFetcher.getJson(`/next-api/cart/getpdfs/${cartId||""}`);
        },

        getCart: async (cartId) => chain(
            await (async () => {
                const cart = await authFetcher.getJson(`/next-api/cart/get/${cartId||""}`);
                if (!cart) {
                    return null;
                }

                return {
                    itemIds: cart.items,
                    ...cart,
                };
            })(),
            fixCartResponse,
        ),
        getCartApproval: async (cartId) => {
            return await authFetcher.getJson(`/next-api/orderApproval/get/${cartId}`);
        },

        createApprovalRequest: async (cartId) => {
            return await authFetcher.postJson(`/next-api/orderApproval/createApprovalRequest`, {
                cartId: "" + cartId,
            });
        },

        getSubscriptionBenefits: ({cartId}) => {
            // /next-api/subscriptions/subscriptionBenefits/cart/{idCart}
            // {totalSavings: 100}
            // {"result": "failure","errors": {"target": "getCartSubscriptionsBenefits","msg": "Not able to locate given Cart Id"}}

            return authFetcher.getJson(`/next-api/subscriptions/subscriptionBenefits/cart/${cartId}`);
        },

        getCartByItem: async (itemId) => chain(
            await (async () => {
                const cart = await authFetcher.getJson(`/next-api/cart/get_cart_by_item/${itemId||""}`);
                if (!cart) {
                    return null;
                }

                return {
                    itemIds: cart.items,
                    ...cart,
                };
            })(),
            fixCartResponse,
        ),
    };
};
